import { Button, Popover, Switch, ToggleButton } from '@tunasong/ui-lib'
import { useId, useState, type FC, type ReactNode } from 'react'
import { usePerformanceConfig } from './part/performance-config.hook.js'
import { usePerformanceViewStore } from './performance-settings.js'
import styles from './display-popover.module.css'

const fontSizes = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
] as const

const notesPlacements = [
  { value: 'beside', label: 'Beside the song' },
  { value: 'panel', label: 'In a panel' },
] as const

const columnFills = [
  { value: 'auto', label: 'Auto' },
  { value: 'balanced', label: 'Balanced' },
] as const

/** A labelled row of toggle buttons; the label names the group. */
const OptionGroup: FC<{ label: string; children: ReactNode }> = ({ label, children }) => {
  const labelId = useId()
  return (
    <div className={styles.row}>
      <span id={labelId} className={styles.label}>
        {label}
      </span>
      <div role="group" aria-labelledby={labelId} className={styles.options}>
        {children}
      </div>
    </div>
  )
}

export interface DisplayPopoverProps {
  /** The chosen parts hide chords (Vocals or Drums alone): offer "Show chords". */
  showChordsToggle?: boolean
}

/**
 * Perform header "Display": how the song is shown. Text size, columns and the beat flash are per device;
 * where notes show is per user.
 */
export const DisplayPopover: FC<DisplayPopoverProps> = ({ showChordsToggle = false }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)
  const {
    fontSize,
    setFontSize,
    columns,
    setColumns,
    columnFill,
    setColumnFill,
    flashBackground,
    setFlashBackground,
    showChords,
    setShowChords,
  } = usePerformanceViewStore()
  const { config, updatePerformanceConfig } = usePerformanceConfig()
  const notesPlacement = config.notesPlacement ?? 'beside'

  return (
    <>
      <Button
        ref={setAnchorEl}
        variant="text"
        color="inherit"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
      >
        Display
      </Button>
      <Popover open={open} onClose={() => setOpen(false)} anchorEl={anchorEl} aria-label="Display">
        <div className={styles.panel}>
          <OptionGroup label="Text size">
            {fontSizes.map(size => (
              <ToggleButton
                key={size.value}
                value={size.value}
                selected={fontSize === size.value}
                onClick={() => setFontSize(size.value)}
              >
                {size.label}
              </ToggleButton>
            ))}
          </OptionGroup>
          <OptionGroup label="Show my notes">
            {notesPlacements.map(placement => (
              <ToggleButton
                key={placement.value}
                value={placement.value}
                selected={notesPlacement === placement.value}
                onClick={() => updatePerformanceConfig({ notesPlacement: placement.value })}
              >
                {placement.label}
              </ToggleButton>
            ))}
          </OptionGroup>
          <OptionGroup label="Columns">
            {[1, 2, 3, 4, 5].map(count => (
              <ToggleButton
                key={count}
                value={String(count)}
                selected={columns === count}
                onClick={() => setColumns(count)}
              >
                {count}
              </ToggleButton>
            ))}
          </OptionGroup>
          <OptionGroup label="Column fill">
            {columnFills.map(fill => (
              <ToggleButton
                key={fill.value}
                value={fill.value}
                selected={columnFill === fill.value}
                onClick={() => setColumnFill(fill.value)}
              >
                {fill.label}
              </ToggleButton>
            ))}
          </OptionGroup>
          {showChordsToggle ? (
            <label className={styles.switchRow}>
              <Switch checked={showChords} onCheckedChange={setShowChords} aria-label="Show chords" />
              <span aria-hidden="true">Show chords</span>
            </label>
          ) : null}
          <label className={styles.switchRow}>
            <Switch
              checked={flashBackground}
              onCheckedChange={setFlashBackground}
              aria-label="Flash the screen on the beat"
            />
            <span aria-hidden="true">Flash the screen on the beat</span>
          </label>
        </div>
      </Popover>
    </>
  )
}

export default DisplayPopover
